import React, { useState, useEffect, createContext, useContext, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { FiHome, FiUserPlus, FiAlertTriangle, FiRefreshCw, FiChevronDown, FiBookOpen, FiSettings, FiClipboard, FiLogOut, FiUser, FiPlus } from 'react-icons/fi';
import { getTenants, syncTenant } from '../api';
import NotificationsDropdown, { addNotification } from './NotificationsDropdown';
import './Layout.css';

const TenantContext = createContext();

export function useTenant() {
  return useContext(TenantContext);
}

const workshopItems = [
  { path: '/discovery-workshop', label: 'Discovery Workshop', icon: FiClipboard },
];

const governanceItems = [
  { path: '/', label: 'Overview', icon: FiHome },
  { path: '/gov-framework', label: 'LZ Assessment', icon: FiBookOpen },
  { path: '/drift', label: 'Drift Posture', icon: FiAlertTriangle },
];

const pinnedItems = [...workshopItems, ...governanceItems];

const analysisItems = [];

const topbarOnlyItems = [
  { path: '/onboarding', label: 'Tenant Onboarding', icon: FiUserPlus },
  { path: '/settings', label: 'Settings', icon: FiSettings },
];

const allNavItems = [...pinnedItems, ...analysisItems, ...topbarOnlyItems];

export default function Layout({ children, onLogout }) {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState(() => {
    return localStorage.getItem('selectedTenantId') || null;
  });
  const [syncing, setSyncing] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const refreshTenants = useCallback(() => {
    return getTenants()
      .then(res => {
        const data = res.data;
        const list = Array.isArray(data) ? data : (data.tenants || []);
        setTenants(list);
        if (list.length > 0) {
          const currentValid = list.find(t => String(t.id || t.tenant_id) === selectedTenantId);
          if (!currentValid) {
            setSelectedTenantId(String(list[0].id || list[0].tenant_id));
          }
        } else {
          setSelectedTenantId(null);
        }
        return list;
      })
      .catch(() => []);
  }, [selectedTenantId]);

  useEffect(() => {
    refreshTenants();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedTenantId) {
      localStorage.setItem('selectedTenantId', selectedTenantId);
    } else {
      localStorage.removeItem('selectedTenantId');
    }
  }, [selectedTenantId]);

  const handleSync = async () => {
    if (!selectedTenantId) return;
    setSyncing(true);
    try {
      await syncTenant(selectedTenantId);
      const tenantName = tenants.find(t => String(t.id) === String(selectedTenantId))?.name || 'Tenant';
      addNotification('sync', 'Sync Complete', `${tenantName} data synchronized successfully`);
    } catch (e) {
      addNotification('security', 'Sync Issue', 'Sync may still be processing. Refreshing data...');
    }
    await refreshTenants();
    setSyncing(false);
  };

  const hasTenants = tenants.length > 0;

  return (
    <TenantContext.Provider value={{ selectedTenantId, setSelectedTenantId, tenants, refreshTenants }}>
      <div className="layout">
        <aside className="sidebar">
          <div className="sidebar-brand">
            <img src="/kgs-logo.png" alt="Kaizentrix Global Solutions" className="brand-logo-img" />
          </div>
          <nav className="sidebar-nav">
            <div className="nav-section-label">Assessment</div>
            {workshopItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                <item.icon className="nav-icon" />
                <span>{item.label}</span>
              </NavLink>
            ))}
            <div className="nav-section-label">Governance</div>
            {governanceItems.map(item => (
              <NavLink
                key={item.path}
                to={item.path}
                end={item.path === '/'}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >
                <item.icon className="nav-icon" />
                <span>{item.label}</span>
              </NavLink>
            ))}
          </nav>
          <div className="sidebar-bottom">
            <NavLink
              to="/onboarding"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              <FiPlus className="nav-icon" />
              <span>Add Tenant</span>
            </NavLink>
            <NavLink
              to="/settings"
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              <FiSettings className="nav-icon" />
              <span>Settings</span>
            </NavLink>
            {onLogout && (
              <button className="nav-link sidebar-logout-btn" onClick={onLogout}>
                <FiLogOut className="nav-icon" />
                <span>Logout</span>
              </button>
            )}
          </div>
        </aside>
        <div className="main-wrapper">
          <header className="topbar">
            <div className="topbar-left">
              <h2 className="page-title">
                {allNavItems.find(n => {
                  if (n.path === '/') return location.pathname === '/';
                  return location.pathname.startsWith(n.path);
                })?.label || 'Dashboard'}
              </h2>
            </div>
            <div className="topbar-right">
              <div className="topbar-actions">
                <NotificationsDropdown />
              </div>
              {localStorage.getItem('lz_username') && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.3rem 0.75rem', background: '#f3f4f6', borderRadius: 8, border: '1px solid #e5e7eb' }}>
                  <FiUser size={14} style={{ color: '#6b7280' }} />
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {localStorage.getItem('lz_username')}
                  </span>
                  {localStorage.getItem('lz_auth_method') === 'azure_ad' && (
                    <span style={{ fontSize: '0.6rem', fontWeight: 700, color: '#fff', background: '#0078d4', padding: '1px 5px', borderRadius: 4, marginLeft: 2 }}>SSO</span>
                  )}
                </div>
              )}
              {hasTenants && (
                <>
                  <div className="tenant-selector" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', background: '#f3f4f6', padding: '0.35rem 0.75rem', borderRadius: 8, border: '2px solid #e5e7eb' }}>
                    <span style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' }}>Tenant</span>
                    <div className="select-wrapper" style={{ minWidth: 180 }}>
                      <select
                        value={selectedTenantId || ''}
                        onChange={e => setSelectedTenantId(e.target.value)}
                        style={{ fontWeight: 600, fontSize: '0.85rem', padding: '0.3rem 2rem 0.3rem 0.5rem' }}
                      >
                        {tenants.map(t => (
                          <option key={t.id || t.tenant_id} value={t.id || t.tenant_id}>
                            {t.name || t.tenant_name || `Tenant ${t.id || t.tenant_id}`}{t.cloud_provider ? ` (${t.cloud_provider.toUpperCase()})` : ''}
                          </option>
                        ))}
                      </select>
                      <FiChevronDown className="select-icon" />
                    </div>
                  </div>
                  <button className="btn btn-primary sync-btn" onClick={handleSync} disabled={syncing}>
                    <FiRefreshCw className={syncing ? 'spinning' : ''} />
                    {syncing ? 'Syncing...' : 'Sync Data'}
                  </button>
                </>
              )}
            </div>
          </header>
          <main className="content">
            {children}
          </main>
        </div>
      </div>
    </TenantContext.Provider>
  );
}
